import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getAdminPropertyPricingDeals,
  saveAdminPropertyDisplayFlags,
  saveAdminPropertyPricingDeals,
} from '../../legacy/api/propertyDetail';

const TABS = [
  { id: 'base', label: 'Base Price' },
  { id: 'seasonal', label: 'Seasonal Price' },
  { id: 'taxes', label: 'Taxes & Fees' },
  { id: 'coupons', label: 'Coupons' },
  { id: 'promotions', label: 'Promotions' },
  { id: 'addons', label: 'Add-ons' },
  { id: 'meals', label: 'Meals' },
  { id: 'display', label: 'Display Controls' },
  { id: 'booking-card', label: 'Booking Card Settings' },
  { id: 'badges', label: 'Feature Badges' },
  { id: 'cancellation', label: 'Cancellation Policy' },
  { id: 'nearby', label: 'Nearby Places' },
  { id: 'reels', label: 'Story/Reels' },
  { id: 'brochure', label: 'Brochure' },
  { id: 'reviews', label: 'Review Sources' },
];

const emptyForm = {
  property: null,
  basePricing: { currency: 'INR', base_price_per_night: '', weekend_price_per_night: '', min_nights: 1, max_guests_included: 2 },
  seasonalPricing: [],
  extraGuestPricing: [],
  mealPlans: [],
  addons: [],
  coupons: [],
  promotions: [],
  fees: [],
  taxes: [],
  displayFlags: {},
  bookingCardSettings: {},
  featureBadges: [],
  cancellationPolicy: [],
  nearbyPoints: [],
  reels: [],
  brochures: [],
  reviewSources: [],
  reviewSummary: '',
  brochureUrl: '',
};

const toInputValue = (value) => (value === null || value === undefined ? '' : value);

const Card = ({ title, description, children }) => (
  <section className="bg-white rounded-4 border shadow-sm p-4 mb-4">
    <h2 className="h5 mb-1">{title}</h2>
    {description ? <p className="text-muted small mb-3">{description}</p> : null}
    {children}
  </section>
);

const RowListEditor = ({ rows = [], onAdd, renderRow, emptyText = 'No rows yet.' }) => (
  <div className="d-grid gap-3">
    {rows.length ? rows.map((row, index) => <div key={row.id || `${index}`}>{renderRow(row, index)}</div>) : <div className="text-muted">{emptyText}</div>}
    <button type="button" className="btn btn-outline-dark" onClick={onAdd}>
      Add Row
    </button>
  </div>
);

const Input = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <label className="form-label">
    <div className="small text-muted mb-1">{label}</div>
    <input className="form-control" type={type} value={toInputValue(value)} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
  </label>
);

const Checkbox = ({ label, checked, onChange }) => (
  <label className="form-check">
    <input className="form-check-input" type="checkbox" checked={!!checked} onChange={(event) => onChange(event.target.checked)} />
    <span className="form-check-label">{label}</span>
  </label>
);

export default function AdminPropertyPricingDealsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('base');
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getAdminPropertyPricingDeals(id)
      .then((payload) => {
        if (!active) return;
        setForm({
          ...emptyForm,
          ...payload,
          reviewSummary: payload.property?.review_summary || '',
          brochureUrl: payload.property?.brochure_url || '',
        });
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'Failed to load pricing deals.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setNestedField = (scope, key, value) => setForm((prev) => ({ ...prev, [scope]: { ...(prev[scope] || {}), [key]: value } }));
  const updateRow = (scope, index, patch) =>
    setForm((prev) => ({
      ...prev,
      [scope]: (prev[scope] || []).map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)),
    }));
  const removeRow = (scope, index) =>
    setForm((prev) => ({ ...prev, [scope]: (prev[scope] || []).filter((_, rowIndex) => rowIndex !== index) }));
  const addRow = (scope, row) => setForm((prev) => ({ ...prev, [scope]: [...(prev[scope] || []), row] }));

  const previewUrl = useMemo(() => `/property/${form.property?.slug || id}`, [form.property?.slug, id]);

  const handleSave = async () => {
    if (!form.basePricing?.base_price_per_night) {
      setError('Base price per night is required.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        reviewSummary: form.reviewSummary,
        brochureUrl: form.brochureUrl,
      };
      const savedPayload = await saveAdminPropertyPricingDeals(id, payload);
      const savedDisplayFlags = await saveAdminPropertyDisplayFlags(id, {
        ...form.displayFlags,
        ...form.bookingCardSettings,
      });
      setForm((prev) => ({
        ...prev,
        ...savedPayload,
        displayFlags: savedDisplayFlags || prev.displayFlags,
        bookingCardSettings: {
          ...(savedPayload?.bookingCardSettings || prev.bookingCardSettings || {}),
        },
        reviewSummary: savedPayload?.property?.review_summary || prev.reviewSummary,
        brochureUrl: savedPayload?.property?.brochure_url || prev.brochureUrl,
      }));
      setSuccess('Pricing & Deals saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to save pricing deals.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">Loading pricing &amp; deals...</div>;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-4">
        <div>
          <h1 className="h3 mb-1">Pricing &amp; Deals</h1>
          <div className="text-muted">
            {form.property?.title} {form.property?.city ? ` - ${form.property.city}` : ''}
          </div>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(`/admin/properties/${id}/edit`)}>
            Back to Property
          </button>
          <Link className="btn btn-outline-dark" to={previewUrl} target="_blank" rel="noreferrer">
            Preview Detail Page
          </Link>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}
      <div className="d-flex flex-wrap gap-2 mb-4">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`btn btn-sm ${activeTab === tab.id ? 'btn-dark' : 'btn-outline-secondary'}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'base' ? (
        <>
          <Card title="Base Pricing" description="Core nightly pricing, weekend uplift, min nights, and included guests.">
            <div className="alert alert-light border small">
              Homevilas margin and customer checkout taxes/fees are managed centrally in <strong>Pricing &amp; Deals &gt; Taxes &amp; Fees</strong>. This page should hold supplier/base pricing only.
            </div>
            <div className="row g-3">
              <div className="col-md-3"><Input label="Currency" value={form.basePricing.currency} onChange={(value) => setNestedField('basePricing', 'currency', value)} /></div>
              <div className="col-md-3"><Input label="Base Price / Night" type="number" value={form.basePricing.base_price_per_night} onChange={(value) => setNestedField('basePricing', 'base_price_per_night', value)} /></div>
              <div className="col-md-3"><Input label="Weekend Price / Night" type="number" value={form.basePricing.weekend_price_per_night} onChange={(value) => setNestedField('basePricing', 'weekend_price_per_night', value)} /></div>
              <div className="col-md-3"><Input label="Min Nights" type="number" value={form.basePricing.min_nights} onChange={(value) => setNestedField('basePricing', 'min_nights', value)} /></div>
              <div className="col-md-3"><Input label="Guests Included" type="number" value={form.basePricing.max_guests_included} onChange={(value) => setNestedField('basePricing', 'max_guests_included', value)} /></div>
            </div>
          </Card>
          <Card title="Extra Guest Pricing" description="Configure occupancy pricing slabs for guests above the included count.">
            <RowListEditor
              rows={form.extraGuestPricing}
              onAdd={() => addRow('extraGuestPricing', { guest_from: '', guest_to: '', extra_fee_per_guest_per_night: '', is_active: true })}
              renderRow={(row, index) => (
                <div className="border rounded-4 p-3">
                  <div className="row g-3">
                    <div className="col-md-3"><Input label="Guest From" type="number" value={row.guest_from} onChange={(value) => updateRow('extraGuestPricing', index, { guest_from: value })} /></div>
                    <div className="col-md-3"><Input label="Guest To" type="number" value={row.guest_to} onChange={(value) => updateRow('extraGuestPricing', index, { guest_to: value })} /></div>
                    <div className="col-md-4"><Input label="Fee / Guest / Night" type="number" value={row.extra_fee_per_guest_per_night} onChange={(value) => updateRow('extraGuestPricing', index, { extra_fee_per_guest_per_night: value })} /></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('extraGuestPricing', index, { is_active: value })} />
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('extraGuestPricing', index)}>Delete</button>
                  </div>
                </div>
              )}
            />
          </Card>
        </>
      ) : null}

      {activeTab === 'seasonal' ? (
        <Card title="Seasonal / Festive / Blackout Pricing" description="Override nightly pricing by date range and priority.">
          <RowListEditor
            rows={form.seasonalPricing}
            onAdd={() => addRow('seasonalPricing', { season_name: '', start_date: '', end_date: '', price_type: 'override', override_price: '', adjustment_percent: '', priority: 100, is_blackout: false, is_active: true })}
            renderRow={(row, index) => (
              <div className="border rounded-4 p-3">
                <div className="row g-3">
                  <div className="col-md-3"><Input label="Season Name" value={row.season_name} onChange={(value) => updateRow('seasonalPricing', index, { season_name: value })} /></div>
                  <div className="col-md-2"><Input label="Start Date" type="date" value={row.start_date} onChange={(value) => updateRow('seasonalPricing', index, { start_date: value })} /></div>
                  <div className="col-md-2"><Input label="End Date" type="date" value={row.end_date} onChange={(value) => updateRow('seasonalPricing', index, { end_date: value })} /></div>
                  <div className="col-md-2"><Input label="Price Type" value={row.price_type} onChange={(value) => updateRow('seasonalPricing', index, { price_type: value })} /></div>
                  <div className="col-md-1"><Input label="Priority" type="number" value={row.priority} onChange={(value) => updateRow('seasonalPricing', index, { priority: value })} /></div>
                  <div className="col-md-2"><Input label="Override Price" type="number" value={row.override_price} onChange={(value) => updateRow('seasonalPricing', index, { override_price: value })} /></div>
                  <div className="col-md-2"><Input label="Adjustment %" type="number" value={row.adjustment_percent} onChange={(value) => updateRow('seasonalPricing', index, { adjustment_percent: value })} /></div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="d-flex gap-3">
                    <Checkbox label="Blackout" checked={row.is_blackout} onChange={(value) => updateRow('seasonalPricing', index, { is_blackout: value })} />
                    <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('seasonalPricing', index, { is_active: value })} />
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('seasonalPricing', index)}>Delete</button>
                </div>
              </div>
            )}
          />
        </Card>
      ) : null}

      {activeTab === 'taxes' ? (
        <>
          <Card title="Fees" description="Cleaning, service, platform, and other booking fees.">
            <RowListEditor
              rows={form.fees}
              onAdd={() => addRow('fees', { fee_name: '', fee_type: 'flat', fee_value: '', is_taxable: false, is_active: true, display_order: 0 })}
              renderRow={(row, index) => (
                <div className="border rounded-4 p-3">
                  <div className="row g-3">
                    <div className="col-md-3"><Input label="Fee Name" value={row.fee_name} onChange={(value) => updateRow('fees', index, { fee_name: value })} /></div>
                    <div className="col-md-3"><Input label="Fee Type" value={row.fee_type} onChange={(value) => updateRow('fees', index, { fee_type: value })} /></div>
                    <div className="col-md-3"><Input label="Fee Value" type="number" value={row.fee_value} onChange={(value) => updateRow('fees', index, { fee_value: value })} /></div>
                    <div className="col-md-3"><Input label="Display Order" type="number" value={row.display_order} onChange={(value) => updateRow('fees', index, { display_order: value })} /></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex gap-3">
                      <Checkbox label="Taxable" checked={row.is_taxable} onChange={(value) => updateRow('fees', index, { is_taxable: value })} />
                      <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('fees', index, { is_active: value })} />
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('fees', index)}>Delete</button>
                  </div>
                </div>
              )}
            />
          </Card>
          <Card title="Taxes" description="GST and tax display on the price box.">
            <RowListEditor
              rows={form.taxes}
              onAdd={() => addRow('taxes', { tax_name: '', tax_type: 'percent', tax_value: '', is_active: true, display_order: 0 })}
              renderRow={(row, index) => (
                <div className="border rounded-4 p-3">
                  <div className="row g-3">
                    <div className="col-md-4"><Input label="Tax Name" value={row.tax_name} onChange={(value) => updateRow('taxes', index, { tax_name: value })} /></div>
                    <div className="col-md-3"><Input label="Tax Type" value={row.tax_type} onChange={(value) => updateRow('taxes', index, { tax_type: value })} /></div>
                    <div className="col-md-3"><Input label="Tax Value" type="number" value={row.tax_value} onChange={(value) => updateRow('taxes', index, { tax_value: value })} /></div>
                    <div className="col-md-2"><Input label="Order" type="number" value={row.display_order} onChange={(value) => updateRow('taxes', index, { display_order: value })} /></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('taxes', index, { is_active: value })} />
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('taxes', index)}>Delete</button>
                  </div>
                </div>
              )}
            />
          </Card>
        </>
      ) : null}

      {activeTab === 'coupons' ? (
        <Card title="Coupons" description="Property coupons, auto-apply rules, and min booking thresholds.">
          <RowListEditor
            rows={form.coupons}
            onAdd={() => addRow('coupons', { code: '', title: '', description: '', discount_type: 'flat', discount_value: '', max_discount_amount: '', min_booking_amount: '', is_active: true, auto_apply: false, stackable: false, is_global: false })}
            renderRow={(row, index) => (
              <div className="border rounded-4 p-3">
                <div className="row g-3">
                  <div className="col-md-2"><Input label="Code" value={row.code} onChange={(value) => updateRow('coupons', index, { code: value.toUpperCase() })} /></div>
                  <div className="col-md-3"><Input label="Title" value={row.title} onChange={(value) => updateRow('coupons', index, { title: value })} /></div>
                  <div className="col-md-2"><Input label="Discount Type" value={row.discount_type} onChange={(value) => updateRow('coupons', index, { discount_type: value })} /></div>
                  <div className="col-md-2"><Input label="Discount Value" type="number" value={row.discount_value} onChange={(value) => updateRow('coupons', index, { discount_value: value })} /></div>
                  <div className="col-md-3"><Input label="Min Booking Amount" type="number" value={row.min_booking_amount} onChange={(value) => updateRow('coupons', index, { min_booking_amount: value })} /></div>
                  <div className="col-md-3"><Input label="Max Discount" type="number" value={row.max_discount_amount} onChange={(value) => updateRow('coupons', index, { max_discount_amount: value })} /></div>
                  <div className="col-md-2"><Input label="Valid From" type="date" value={row.valid_from} onChange={(value) => updateRow('coupons', index, { valid_from: value })} /></div>
                  <div className="col-md-2"><Input label="Valid To" type="date" value={row.valid_to} onChange={(value) => updateRow('coupons', index, { valid_to: value })} /></div>
                  <div className="col-md-2"><Input label="Usage Limit" type="number" value={row.usage_limit} onChange={(value) => updateRow('coupons', index, { usage_limit: value })} /></div>
                  <div className="col-md-3"><Input label="Description" value={row.description} onChange={(value) => updateRow('coupons', index, { description: value })} /></div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-3">
                  <div className="d-flex gap-3 flex-wrap">
                    <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('coupons', index, { is_active: value })} />
                    <Checkbox label="Auto Apply" checked={row.auto_apply} onChange={(value) => updateRow('coupons', index, { auto_apply: value })} />
                    <Checkbox label="Stackable" checked={row.stackable} onChange={(value) => updateRow('coupons', index, { stackable: value })} />
                    <Checkbox label="Global" checked={row.is_global} onChange={(value) => updateRow('coupons', index, { is_global: value })} />
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('coupons', index)}>Delete</button>
                </div>
              </div>
            )}
          />
        </Card>
      ) : null}

      {activeTab === 'promotions' ? (
        <Card title="Promotions & Bank Offers" description="Stay offers, bank offers, instant discounts, and custom campaigns.">
          <RowListEditor
            rows={form.promotions}
            onAdd={() => addRow('promotions', { title: '', subtitle: '', description: '', offer_type: 'instant_discount', discount_type: 'flat', discount_value: '', allow_coupon_stack: false, is_active: true, display_in_detail_page: true, is_global: false })}
            renderRow={(row, index) => (
              <div className="border rounded-4 p-3">
                <div className="row g-3">
                  <div className="col-md-3"><Input label="Title" value={row.title} onChange={(value) => updateRow('promotions', index, { title: value })} /></div>
                  <div className="col-md-3"><Input label="Subtitle" value={row.subtitle} onChange={(value) => updateRow('promotions', index, { subtitle: value })} /></div>
                  <div className="col-md-2"><Input label="Offer Type" value={row.offer_type} onChange={(value) => updateRow('promotions', index, { offer_type: value })} /></div>
                  <div className="col-md-2"><Input label="Discount Type" value={row.discount_type} onChange={(value) => updateRow('promotions', index, { discount_type: value })} /></div>
                  <div className="col-md-2"><Input label="Discount Value" type="number" value={row.discount_value} onChange={(value) => updateRow('promotions', index, { discount_value: value })} /></div>
                  <div className="col-md-2"><Input label="Start Date" type="date" value={row.start_date} onChange={(value) => updateRow('promotions', index, { start_date: value })} /></div>
                  <div className="col-md-2"><Input label="End Date" type="date" value={row.end_date} onChange={(value) => updateRow('promotions', index, { end_date: value })} /></div>
                  <div className="col-md-6"><Input label="Description" value={row.description} onChange={(value) => updateRow('promotions', index, { description: value })} /></div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-3">
                  <div className="d-flex gap-3 flex-wrap">
                    <Checkbox label="Display on Detail Page" checked={row.display_in_detail_page !== false} onChange={(value) => updateRow('promotions', index, { display_in_detail_page: value })} />
                    <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('promotions', index, { is_active: value })} />
                    <Checkbox label="Allow Coupon Stack" checked={row.allow_coupon_stack} onChange={(value) => updateRow('promotions', index, { allow_coupon_stack: value })} />
                    <Checkbox label="Global" checked={row.is_global} onChange={(value) => updateRow('promotions', index, { is_global: value })} />
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('promotions', index)}>Delete</button>
                </div>
              </div>
            )}
          />
        </Card>
      ) : null}

      {activeTab === 'addons' ? (
        <Card title="Add-ons" description="Merchandise paid services and upgrades on the detail page.">
          <RowListEditor
            rows={form.addons}
            onAdd={() => addRow('addons', { title: '', short_description: '', image_url: '', amount: '', pricing_type: 'per_booking', max_quantity: 1, is_active: true })}
            renderRow={(row, index) => (
              <div className="border rounded-4 p-3">
                <div className="row g-3">
                  <div className="col-md-3"><Input label="Title" value={row.title} onChange={(value) => updateRow('addons', index, { title: value })} /></div>
                  <div className="col-md-3"><Input label="Subtitle" value={row.short_description} onChange={(value) => updateRow('addons', index, { short_description: value })} /></div>
                  <div className="col-md-3"><Input label="Image URL" value={row.image_url} onChange={(value) => updateRow('addons', index, { image_url: value })} /></div>
                  <div className="col-md-1"><Input label="Amount" type="number" value={row.amount} onChange={(value) => updateRow('addons', index, { amount: value })} /></div>
                  <div className="col-md-1"><Input label="Type" value={row.pricing_type} onChange={(value) => updateRow('addons', index, { pricing_type: value })} /></div>
                  <div className="col-md-1"><Input label="Max Qty" type="number" value={row.max_quantity} onChange={(value) => updateRow('addons', index, { max_quantity: value })} /></div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('addons', index, { is_active: value })} />
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('addons', index)}>Delete</button>
                </div>
              </div>
            )}
          />
        </Card>
      ) : null}

      {activeTab === 'meals' ? (
        <Card title="Meal Plans" description="Property-only or package-style meal pricing.">
          <RowListEditor
            rows={form.mealPlans}
            onAdd={() => addRow('mealPlans', { title: '', description: '', price_type: 'per_booking', amount: '', is_default: false, is_active: true })}
            renderRow={(row, index) => (
              <div className="border rounded-4 p-3">
                <div className="row g-3">
                  <div className="col-md-3"><Input label="Title" value={row.title} onChange={(value) => updateRow('mealPlans', index, { title: value })} /></div>
                  <div className="col-md-4"><Input label="Description" value={row.description} onChange={(value) => updateRow('mealPlans', index, { description: value })} /></div>
                  <div className="col-md-2"><Input label="Price Type" value={row.price_type} onChange={(value) => updateRow('mealPlans', index, { price_type: value })} /></div>
                  <div className="col-md-2"><Input label="Amount" type="number" value={row.amount} onChange={(value) => updateRow('mealPlans', index, { amount: value })} /></div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="d-flex gap-3">
                    <Checkbox label="Default" checked={row.is_default} onChange={(value) => updateRow('mealPlans', index, { is_default: value })} />
                    <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('mealPlans', index, { is_active: value })} />
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('mealPlans', index)}>Delete</button>
                </div>
              </div>
            )}
          />
        </Card>
      ) : null}

      {activeTab === 'display' ? (
        <Card title="Display Controls" description="Enable or disable sections per property.">
          <div className="row g-3">
            {Object.keys(form.displayFlags || {})
              .filter((key) => typeof form.displayFlags[key] === 'boolean')
              .map((key) => (
              <div key={key} className="col-md-4">
                <Checkbox label={key.replace(/_/g, ' ')} checked={form.displayFlags[key]} onChange={(value) => setNestedField('displayFlags', key, value)} />
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {activeTab === 'booking-card' ? (
        <Card title="Booking Card Settings" description="Control tax label, CTA copy, trust strip, and support links.">
          <div className="row g-3">
            <div className="col-md-3"><Input label="Tax Display Mode" value={form.bookingCardSettings.tax_display_mode} onChange={(value) => setNestedField('bookingCardSettings', 'tax_display_mode', value)} /></div>
            <div className="col-md-3"><Input label="CTA Label" value={form.bookingCardSettings.booking_cta_label} onChange={(value) => setNestedField('bookingCardSettings', 'booking_cta_label', value)} /></div>
            <div className="col-md-6"><Input label="Trust Strip Text" value={form.bookingCardSettings.trust_strip_text} onChange={(value) => setNestedField('bookingCardSettings', 'trust_strip_text', value)} /></div>
            <div className="col-md-3"><Input label="Contact Phone" value={form.bookingCardSettings.contact_phone} onChange={(value) => setNestedField('bookingCardSettings', 'contact_phone', value)} /></div>
            <div className="col-md-3"><Input label="WhatsApp" value={form.bookingCardSettings.contact_whatsapp} onChange={(value) => setNestedField('bookingCardSettings', 'contact_whatsapp', value)} /></div>
            <div className="col-md-3"><Input label="Call Button Label" value={form.bookingCardSettings.contact_cta_label} onChange={(value) => setNestedField('bookingCardSettings', 'contact_cta_label', value)} /></div>
            <div className="col-md-3"><Input label="Message Button Label" value={form.bookingCardSettings.message_cta_label} onChange={(value) => setNestedField('bookingCardSettings', 'message_cta_label', value)} /></div>
            <div className="col-md-3"><Input label="Support Target" value={form.bookingCardSettings.support_target} onChange={(value) => setNestedField('bookingCardSettings', 'support_target', value)} /></div>
          </div>
          <div className="mt-3">
            <Checkbox label="Show Savings Banner" checked={form.bookingCardSettings.show_savings_banner} onChange={(value) => setNestedField('bookingCardSettings', 'show_savings_banner', value)} />
          </div>
        </Card>
      ) : null}

      {activeTab === 'badges' ? (
        <Card title="Feature Badges" description="Premium badges under the property heading.">
          <RowListEditor
            rows={form.featureBadges}
            onAdd={() => addRow('featureBadges', { badge_type: '', badge_title: '', badge_subtitle: '', icon_name: 'bi-award', is_active: true })}
            renderRow={(row, index) => (
              <div className="border rounded-4 p-3">
                <div className="row g-3">
                  <div className="col-md-3"><Input label="Badge Type" value={row.badge_type} onChange={(value) => updateRow('featureBadges', index, { badge_type: value })} /></div>
                  <div className="col-md-3"><Input label="Title" value={row.badge_title} onChange={(value) => updateRow('featureBadges', index, { badge_title: value })} /></div>
                  <div className="col-md-4"><Input label="Subtitle" value={row.badge_subtitle} onChange={(value) => updateRow('featureBadges', index, { badge_subtitle: value })} /></div>
                  <div className="col-md-2"><Input label="Icon" value={row.icon_name} onChange={(value) => updateRow('featureBadges', index, { icon_name: value })} /></div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('featureBadges', index, { is_active: value })} />
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('featureBadges', index)}>Delete</button>
                </div>
              </div>
            )}
          />
        </Card>
      ) : null}

      {activeTab === 'cancellation' ? (
        <Card title="Cancellation Policy" description="Boxed cancellation rules shown on the detail page.">
          <RowListEditor
            rows={form.cancellationPolicy}
            onAdd={() => addRow('cancellationPolicy', { rule_type: 'custom', label: '', from_days: '', to_days: '', refund_percent: '', description: '', is_active: true })}
            renderRow={(row, index) => (
              <div className="border rounded-4 p-3">
                <div className="row g-3">
                  <div className="col-md-2"><Input label="Type" value={row.rule_type} onChange={(value) => updateRow('cancellationPolicy', index, { rule_type: value })} /></div>
                  <div className="col-md-4"><Input label="Label" value={row.label} onChange={(value) => updateRow('cancellationPolicy', index, { label: value })} /></div>
                  <div className="col-md-2"><Input label="From Days" type="number" value={row.from_days} onChange={(value) => updateRow('cancellationPolicy', index, { from_days: value })} /></div>
                  <div className="col-md-2"><Input label="To Days" type="number" value={row.to_days} onChange={(value) => updateRow('cancellationPolicy', index, { to_days: value })} /></div>
                  <div className="col-md-2"><Input label="Refund %" type="number" value={row.refund_percent} onChange={(value) => updateRow('cancellationPolicy', index, { refund_percent: value })} /></div>
                  <div className="col-md-12"><Input label="Description" value={row.description} onChange={(value) => updateRow('cancellationPolicy', index, { description: value })} /></div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('cancellationPolicy', index, { is_active: value })} />
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('cancellationPolicy', index)}>Delete</button>
                </div>
              </div>
            )}
          />
        </Card>
      ) : null}

      {activeTab === 'nearby' ? (
        <Card title="Nearby Places" description="Manage points of interest below the map.">
          <RowListEditor rows={form.nearbyPoints} onAdd={() => addRow('nearbyPoints', { title: '', category: '', distance_km: '' })} renderRow={(row, index) => (
            <div className="border rounded-4 p-3">
              <div className="row g-3">
                <div className="col-md-4"><Input label="Title" value={row.title} onChange={(value) => updateRow('nearbyPoints', index, { title: value })} /></div>
                <div className="col-md-4"><Input label="Category" value={row.category} onChange={(value) => updateRow('nearbyPoints', index, { category: value })} /></div>
                <div className="col-md-2"><Input label="Distance (km)" type="number" value={row.distance_km} onChange={(value) => updateRow('nearbyPoints', index, { distance_km: value })} /></div>
                <div className="col-md-2 d-flex align-items-end"><button type="button" className="btn btn-outline-danger w-100" onClick={() => removeRow('nearbyPoints', index)}>Delete</button></div>
              </div>
            </div>
          )} />
        </Card>
      ) : null}

      {activeTab === 'reels' ? (
        <Card title="Story / Reels" description="Manage vertical story cards and walkthrough videos.">
          <RowListEditor rows={form.reels} onAdd={() => addRow('reels', { title: '', cover_image_url: '', video_url: '', source_type: 'upload', creator_name: '', tag_label: '', is_active: true })} renderRow={(row, index) => (
            <div className="border rounded-4 p-3">
              <div className="row g-3">
                <div className="col-md-3"><Input label="Title" value={row.title} onChange={(value) => updateRow('reels', index, { title: value })} /></div>
                <div className="col-md-3"><Input label="Cover Image URL" value={row.cover_image_url} onChange={(value) => updateRow('reels', index, { cover_image_url: value })} /></div>
                <div className="col-md-3"><Input label="Video URL" value={row.video_url} onChange={(value) => updateRow('reels', index, { video_url: value })} /></div>
                <div className="col-md-3"><Input label="Source Type" value={row.source_type} onChange={(value) => updateRow('reels', index, { source_type: value })} /></div>
                <div className="col-md-3"><Input label="Creator" value={row.creator_name} onChange={(value) => updateRow('reels', index, { creator_name: value })} /></div>
                <div className="col-md-3"><Input label="Tag Label" value={row.tag_label} onChange={(value) => updateRow('reels', index, { tag_label: value })} /></div>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-3">
                <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('reels', index, { is_active: value })} />
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('reels', index)}>Delete</button>
              </div>
            </div>
          )} />
        </Card>
      ) : null}

      {activeTab === 'brochure' ? (
        <Card title="Brochure" description="Control brochure CTA visibility and per-property brochure files.">
          <div className="row g-3 mb-4">
            <div className="col-md-8"><Input label="Primary Brochure URL" value={form.brochureUrl} onChange={(value) => setField('brochureUrl', value)} /></div>
          </div>
          <RowListEditor rows={form.brochures} onAdd={() => addRow('brochures', { file_url: '', file_name: '', is_active: true })} renderRow={(row, index) => (
            <div className="border rounded-4 p-3">
              <div className="row g-3">
                <div className="col-md-8"><Input label="File URL" value={row.file_url} onChange={(value) => updateRow('brochures', index, { file_url: value })} /></div>
                <div className="col-md-3"><Input label="File Name" value={row.file_name} onChange={(value) => updateRow('brochures', index, { file_name: value })} /></div>
                <div className="col-md-1 d-flex align-items-end"><button type="button" className="btn btn-outline-danger w-100" onClick={() => removeRow('brochures', index)}>Delete</button></div>
              </div>
            </div>
          )} />
        </Card>
      ) : null}

      {activeTab === 'reviews' ? (
        <Card title="Review Sources" description="Source-wise badges and optional AI summary text.">
          <div className="row g-3 mb-4">
            <div className="col-md-12"><Input label="AI Summary" value={form.reviewSummary} onChange={(value) => setField('reviewSummary', value)} /></div>
          </div>
          <RowListEditor rows={form.reviewSources} onAdd={() => addRow('reviewSources', { source_name: '', source_rating: '', source_review_count: '', source_url: '', summary_text: '', is_active: true })} renderRow={(row, index) => (
            <div className="border rounded-4 p-3">
              <div className="row g-3">
                <div className="col-md-3"><Input label="Source Name" value={row.source_name} onChange={(value) => updateRow('reviewSources', index, { source_name: value })} /></div>
                <div className="col-md-2"><Input label="Rating" type="number" value={row.source_rating} onChange={(value) => updateRow('reviewSources', index, { source_rating: value })} /></div>
                <div className="col-md-2"><Input label="Review Count" type="number" value={row.source_review_count} onChange={(value) => updateRow('reviewSources', index, { source_review_count: value })} /></div>
                <div className="col-md-5"><Input label="Source URL" value={row.source_url} onChange={(value) => updateRow('reviewSources', index, { source_url: value })} /></div>
                <div className="col-md-12"><Input label="Summary Text" value={row.summary_text} onChange={(value) => updateRow('reviewSources', index, { summary_text: value })} /></div>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-3">
                <Checkbox label="Active" checked={row.is_active !== false} onChange={(value) => updateRow('reviewSources', index, { is_active: value })} />
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow('reviewSources', index)}>Delete</button>
              </div>
            </div>
          )} />
        </Card>
      ) : null}
    </div>
  );
}
